import { Component, type ReactNode } from 'react';

/**
 * Contains a render crash instead of losing the page to it.
 *
 * React unmounts the whole tree when a render throws, and the console had
 * nothing to catch that — so one bad value in one panel turned Communications
 * into a white screen with no way back but a reload, and nothing on screen
 * saying what happened. An agent in the middle of a conversation should never
 * be shown that.
 *
 * It also SAYS what broke. The message is the shortest path from "it went
 * blank" to a fix, and asking somebody to open DevTools is asking them to do
 * the reporting for us.
 */
interface Props {
  children: ReactNode;
  /** What was being shown, in the words of the screen: "Notifications". */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // The stack is what names the component; console.error survives to the
    // browser's own log where a user can copy it out of a screenshot.
    console.error(`[crm] ${this.props.label ?? 'screen'} crashed`, error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="err-boundary" role="alert">
        <strong>{this.props.label ?? 'This section'} hit an error.</strong>
        <span className="err-boundary-msg">{error.message || String(error)}</span>
        <div className="err-boundary-actions">
          <button type="button" className="btn btn-ghost" onClick={() => this.setState({ error: null })}>
            Try again
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => window.location.reload()}>
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
